import {
  AlertCircle,
  CheckCircle2,
  Copy,
  KeyRound,
  Plus,
  RefreshCw,
  Wifi,
  WifiOff
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import {
  cancelAgentRunnerJob,
  createAgentRunner,
  getAgentRunnerJobs,
  getAgentRunners,
  listProjectRepoMappings,
  retryAgentRunnerJob,
  revokeAgentRunner,
  rotateAgentRunner,
  setAgentRunnerRepositoryAccess,
  startAgentRunnerRepositoryCheck,
  type AgentRunnerJobRow,
  type AgentRunnerRow,
  type ProjectRepoMappingRow
} from '../api';
import { useFeedback } from '../contexts/FeedbackContext';
import { useSession } from '../contexts/SessionContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { formatRelativeDate } from '../utils/formatters';

type RequestError = {
  response?: { data?: { message?: string } };
  message?: string;
};
type RunnerAvailability = AgentRunnerRow['availability'];
const ACTIVE_JOB_REFRESH_MS = 15_000;

export function hasOutstandingRunnerJobs(jobs: AgentRunnerJobRow[]) {
  return jobs.some(
    (job) =>
      job.status === 'queued' ||
      job.status === 'leased' ||
      job.status === 'cancel_requested'
  );
}

export function currentRunnerAvailability(
  runner: AgentRunnerRow,
  now = Date.now()
): RunnerAvailability {
  if (runner.status === 'revoked') return 'revoked';
  if (new Date(runner.tokenExpiresAt).getTime() <= now) return 'expired';
  if (!runner.lastHeartbeatAt) return 'awaiting_check_in';
  return now - new Date(runner.lastHeartbeatAt).getTime() <= 90_000
    ? 'online'
    : 'offline';
}

export function repositoryCheckFailure(failureCode: string) {
  const failures: Record<string, { title: string; nextStep: string }> = {
    access_denied: {
      title: 'Repository link or access needs attention',
      nextStep:
        'Check the repository name and read-only Git credentials on the runner.'
    },
    network_unavailable: {
      title: 'Runner could not reach GitHub',
      nextStep:
        'Check DNS, outbound HTTPS and proxy settings on the runner machine.'
    },
    branch_unavailable: {
      title: 'GitHub returned no usable default branch',
      nextStep: 'Check the repository default branch, then run the check again.'
    },
    repository_access_removed: {
      title: 'Repository access was removed',
      nextStep:
        'Ask a workspace admin to grant this runner access before starting a new check.'
    },
    runner_lost: {
      title: 'Runner stopped checking in',
      nextStep:
        'Check the runner service and its connection to Cindro, then retry the check.'
    },
    lookup_failed: {
      title: 'Git could not read the repository',
      nextStep:
        'Check the linked repository and Git installation on the runner, then retry.'
    }
  };
  return (
    failures[failureCode] || {
      title: 'Repository check did not finish',
      nextStep: 'Check the runner status and job history before retrying.'
    }
  );
}

function errorMessage(error: unknown) {
  const failure = error as RequestError;
  return (
    failure.response?.data?.message ||
    failure.message ||
    'Could not update the runner'
  );
}

function availabilityLabel(availability: RunnerAvailability) {
  if (availability === 'online') return 'Checked in recently';
  if (availability === 'awaiting_check_in') return 'Waiting for first check-in';
  if (availability === 'offline') return 'No recent check-in';
  if (availability === 'expired') return 'Credential expired';
  return 'Revoked';
}

export function AgentRunnerPanel() {
  const { session } = useSession();
  const { activeOrganization } = useWorkspace();
  const { confirm, toast } = useFeedback();
  const canManage =
    session?.user?.role === 'owner' || session?.user?.role === 'admin';
  const [runners, setRunners] = useState<AgentRunnerRow[]>([]);
  const [repositories, setRepositories] = useState<ProjectRepoMappingRow[]>([]);
  const [jobs, setJobs] = useState<AgentRunnerJobRow[]>([]);
  const [selectedRunnerId, setSelectedRunnerId] = useState('');
  const [selectedMappingId, setSelectedMappingId] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [retryingJobId, setRetryingJobId] = useState('');
  const [cancellingJobId, setCancellingJobId] = useState('');
  const [editingScopeRunnerId, setEditingScopeRunnerId] = useState('');
  const [scopeDraftIds, setScopeDraftIds] = useState<string[]>([]);
  const [expandedHistoryJobId, setExpandedHistoryJobId] = useState('');
  const [error, setError] = useState('');
  const [oneTimeToken, setOneTimeToken] = useState<{
    name: string;
    token: string;
  } | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    setOneTimeToken(null);
    setEditingScopeRunnerId('');
    setScopeDraftIds([]);
  }, [activeOrganization?.id]);

  const load = useCallback(async () => {
    if (!session?.accessToken || !activeOrganization?.id) return;
    setLoading(true);
    try {
      setRunners(
        await getAgentRunners(session.accessToken, activeOrganization.id)
      );
      setRepositories(
        (
          await listProjectRepoMappings(session.accessToken, {
            organizationId: activeOrganization.id
          })
        ).filter((mapping) => mapping.provider === 'github')
      );
      setJobs(
        await getAgentRunnerJobs(session.accessToken, activeOrganization.id)
      );
      setError('');
    } catch (failure: unknown) {
      setError(errorMessage(failure));
    } finally {
      setLoading(false);
    }
  }, [activeOrganization?.id, session?.accessToken]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (
      !session?.accessToken ||
      !activeOrganization?.id ||
      !hasOutstandingRunnerJobs(jobs)
    )
      return;
    let mounted = true;
    let requestInFlight = false;
    const refreshJobs = async () => {
      if (!mounted || document.visibilityState === 'hidden' || requestInFlight)
        return;
      requestInFlight = true;
      try {
        const latestJobs = await getAgentRunnerJobs(
          session.accessToken,
          activeOrganization.id
        );
        if (mounted) setJobs(latestJobs);
      } catch {
        // Keep the last known state and retry on the next visible refresh.
      } finally {
        requestInFlight = false;
      }
    };
    const timer = window.setInterval(() => {
      void refreshJobs();
    }, ACTIVE_JOB_REFRESH_MS);
    const refreshWhenVisible = () => {
      if (document.visibilityState !== 'hidden') void refreshJobs();
    };
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      mounted = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [activeOrganization?.id, jobs, session?.accessToken]);

  const register = async (event: React.FormEvent) => {
    event.preventDefault();
    if (
      !session?.accessToken ||
      !activeOrganization?.id ||
      name.trim().length < 2
    )
      return;
    setBusy(true);
    try {
      const created = await createAgentRunner(
        session.accessToken,
        activeOrganization.id,
        name.trim()
      );
      setOneTimeToken({ name: created.name, token: created.runnerToken || '' });
      setName('');
      await load();
    } catch (failure: unknown) {
      toast('error', errorMessage(failure));
    } finally {
      setBusy(false);
    }
  };

  const rotate = async (runner: AgentRunnerRow) => {
    if (
      !session?.accessToken ||
      !(await confirm({
        title: `Replace ${runner.name}’s credential?`,
        message:
          'The current credential will stop working immediately. The new one is shown once, so plan to update the machine before it checks in again.',
        confirmLabel: 'Replace credential'
      }))
    )
      return;
    setBusy(true);
    try {
      const updated = await rotateAgentRunner(
        session.accessToken,
        runner.agentRunnerId
      );
      setOneTimeToken({ name: updated.name, token: updated.runnerToken || '' });
      await load();
    } catch (failure: unknown) {
      toast('error', errorMessage(failure));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (runner: AgentRunnerRow) => {
    if (
      !session?.accessToken ||
      !(await confirm({
        title: `Revoke ${runner.name}?`,
        message:
          'This stops the machine from checking in. It cannot be undone; register it again to reconnect.',
        confirmLabel: 'Revoke runner',
        danger: true
      }))
    )
      return;
    setBusy(true);
    try {
      await revokeAgentRunner(session.accessToken, runner.agentRunnerId);
      setOneTimeToken(null);
      await load();
      toast('success', `${runner.name} can no longer check in`);
    } catch (failure: unknown) {
      toast('error', errorMessage(failure));
    } finally {
      setBusy(false);
    }
  };

  const copyToken = async () => {
    if (!oneTimeToken?.token) return;
    try {
      await navigator.clipboard.writeText(oneTimeToken.token);
      toast('success', 'Credential copied');
    } catch {
      toast('error', 'Could not copy the credential');
    }
  };

  const runRepositoryCheck = async (event: React.FormEvent) => {
    event.preventDefault();
    if (
      !session?.accessToken ||
      !activeOrganization?.id ||
      !selectedRunnerId ||
      !selectedMappingId
    )
      return;
    setBusy(true);
    try {
      await startAgentRunnerRepositoryCheck(session.accessToken, {
        organizationId: activeOrganization.id,
        agentRunnerId: selectedRunnerId,
        connectorResourceMappingId: selectedMappingId
      });
      setJobs(
        await getAgentRunnerJobs(session.accessToken, activeOrganization.id)
      );
      toast('success', 'Repository check sent to the runner');
    } catch (failure: unknown) {
      toast('error', errorMessage(failure));
    } finally {
      setBusy(false);
    }
  };

  const retryCheck = async (job: AgentRunnerJobRow) => {
    if (!session?.accessToken) return;
    const runnerId = selectedRunnerId || job.agentRunnerId;
    const runner = runners.find(
      (candidate) => candidate.agentRunnerId === runnerId
    );
    if (
      !runner ||
      currentRunnerAvailability(runner) !== 'online' ||
      !runner.capabilities.includes('repo.readonly-check')
    ) {
      toast('error', 'Choose a ready runner before retrying this check');
      return;
    }
    if (
      !job.connectorResourceMappingId ||
      !runner.allowedConnectorResourceMappingIds?.includes(
        job.connectorResourceMappingId
      )
    ) {
      toast(
        'error',
        'That runner is not allowed to check this repository. Ask a workspace admin to grant access.'
      );
      return;
    }
    setRetryingJobId(job.agentRunnerJobId);
    try {
      await retryAgentRunnerJob(
        session.accessToken,
        job.agentRunnerJobId,
        runnerId
      );
      await load();
      toast('success', `Check queued again for ${job.repository}`);
    } catch (failure: unknown) {
      toast('error', errorMessage(failure));
    } finally {
      setRetryingJobId('');
    }
  };

  const editRepositoryScope = (runner: AgentRunnerRow) => {
    setEditingScopeRunnerId(runner.agentRunnerId);
    setScopeDraftIds(runner.allowedConnectorResourceMappingIds || []);
  };

  const saveRepositoryScope = async (runner: AgentRunnerRow) => {
    if (!session?.accessToken) return;
    setBusy(true);
    try {
      await setAgentRunnerRepositoryAccess(
        session.accessToken,
        runner.agentRunnerId,
        scopeDraftIds
      );
      await load();
      setEditingScopeRunnerId('');
      toast('success', `Repository access updated for ${runner.name}`);
    } catch (failure: unknown) {
      toast('error', errorMessage(failure));
    } finally {
      setBusy(false);
    }
  };

  const cancelCheck = async (job: AgentRunnerJobRow) => {
    const queued = job.status === 'queued';
    if (
      !session?.accessToken ||
      !(await confirm({
        title: queued
          ? 'Cancel this repository check?'
          : 'Request a stop for this check?',
        message: queued
          ? 'It has not started yet, so it will be cancelled immediately and kept in the history.'
          : 'The runner is doing a read-only Git lookup. It will finish that short operation, then confirm the stop on its next check-in. If it loses contact first, Cindro will show the check as interrupted.',
        confirmLabel: queued ? 'Cancel check' : 'Request stop'
      }))
    )
      return;
    setCancellingJobId(job.agentRunnerJobId);
    try {
      const cancelled = await cancelAgentRunnerJob(
        session.accessToken,
        job.agentRunnerJobId
      );
      await load();
      toast(
        'success',
        cancelled.status === 'cancel_requested'
          ? `Stop requested for ${job.repository}`
          : `Check cancelled for ${job.repository}`
      );
    } catch (failure: unknown) {
      toast('error', errorMessage(failure));
    } finally {
      setCancellingJobId('');
    }
  };

  return (
    <section className="agentRunnerPanel">
      <div className="agentRunnerBoundary">
        <AlertCircle size={17} />
        <div>
          <strong>Current jobs only check repository heads</strong>
          <span>
            A runner reads the default branch and commit for a linked GitHub
            repository. It does not download source, run project code, or write
            to GitHub. Coding-agent jobs are not available yet. The Linux
            package is a private Actions artifact kept for 30 days, and private
            repositories need read-only Git credentials on the runner machine.
          </span>
        </div>
      </div>

      {oneTimeToken ? (
        <div className="agentRunnerToken" role="status">
          <div>
            <KeyRound size={17} />
            <span>
              <strong>Save this credential for {oneTimeToken.name}</strong>
              <small>
                Cindro will not show it again. Store it as a protected secret on
                that machine; never paste it into a task or commit it.
              </small>
            </span>
          </div>
          <code>
            {oneTimeToken.token ||
              'Credential was not returned. Refresh the list and rotate it again.'}
          </code>
          <div className="agentRunnerTokenActions">
            <button
              type="button"
              className="secondaryButton"
              disabled={!oneTimeToken.token}
              onClick={() => void copyToken()}
            >
              <Copy size={14} />
              Copy credential
            </button>
            <button
              type="button"
              className="textButton"
              onClick={() => setOneTimeToken(null)}
            >
              I’ve saved it
            </button>
          </div>
        </div>
      ) : null}

      {canManage ? (
        <form
          className="agentRunnerRegister"
          onSubmit={(event) => void register(event)}
        >
          <label>
            <span>Machine name</span>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={120}
              minLength={2}
              placeholder="e.g. build-vm-west-1"
              required
            />
          </label>
          <button
            type="submit"
            className="primaryButton"
            disabled={busy || !name.trim()}
          >
            <Plus size={15} />
            Register test machine
          </button>
        </form>
      ) : (
        <p className="agentRunnerEmpty">
          Only workspace owners and admins can manage machines or start, retry
          and cancel checks.
        </p>
      )}

      {error ? (
        <div className="connectionNotice">
          <AlertCircle size={15} />
          <div>
            <strong>Could not load registered machines</strong>
            <span>{error}</span>
            <button
              type="button"
              className="textButton"
              onClick={() => void load()}
            >
              Try again
            </button>
          </div>
        </div>
      ) : null}
      <div className="agentRunnerListHeading">
        <strong>Registered machines</strong>
        <button
          type="button"
          className="textButton"
          disabled={loading || busy}
          onClick={() => void load()}
        >
          <RefreshCw size={13} />
          Refresh
        </button>
      </div>
      {loading ? (
        <p className="agentRunnerEmpty">Loading runners…</p>
      ) : runners.length === 0 ? (
        <p className="agentRunnerEmpty">
          No runners registered. Add a customer-managed machine to start a
          read-only repository check.
        </p>
      ) : (
        <div className="agentRunnerList">
          {runners.map((runner) => {
            const availability = currentRunnerAvailability(runner, now);
            return (
              <article
                className={`agentRunnerRow agentRunnerRow--${availability}`}
                key={runner.agentRunnerId}
              >
                <div className="agentRunnerIdentity">
                  <span className="agentRunnerStatusIcon">
                    {availability === 'online' ? (
                      <Wifi size={16} />
                    ) : availability === 'revoked' ||
                      availability === 'expired' ? (
                      <WifiOff size={16} />
                    ) : (
                      <AlertCircle size={16} />
                    )}
                  </span>
                  <div>
                    <strong>{runner.name}</strong>
                    <span>
                      {runner.platform
                        ? `${runner.platform}${runner.runnerVersion ? ` · v${runner.runnerVersion}` : ''}`
                        : 'No machine details reported yet'}
                    </span>
                  </div>
                </div>
                <div className="agentRunnerAvailability">
                  <span>
                    {availability === 'online' ? (
                      <CheckCircle2 size={13} />
                    ) : null}
                    {availabilityLabel(availability)}
                  </span>
                  <small>
                    {runner.lastHeartbeatAt
                      ? `Last check-in ${formatRelativeDate(runner.lastHeartbeatAt)}`
                      : 'No check-in received'}
                  </small>
                </div>
                <div className="agentRunnerMeta">
                  <span>
                    Credential ends{' '}
                    {new Date(runner.tokenExpiresAt).toLocaleDateString()}
                  </span>
                  <small>
                    {runner.capabilities.length
                      ? `Reports: ${runner.capabilities.join(', ')}`
                      : 'No capabilities reported'}
                  </small>
                  <small>
                    {runner.allowedConnectorResourceMappingIds?.length || 0}{' '}
                    repositories allowed
                  </small>
                </div>
                {runner.status === 'active' && canManage ? (
                  <div className="agentRunnerActions">
                    <button
                      type="button"
                      className="secondaryButton"
                      disabled={busy}
                      onClick={() => editRepositoryScope(runner)}
                    >
                      Repositories
                    </button>
                    <button
                      type="button"
                      className="secondaryButton"
                      disabled={busy}
                      onClick={() => void rotate(runner)}
                    >
                      Replace credential
                    </button>
                    <button
                      type="button"
                      className="textButton"
                      disabled={busy}
                      onClick={() => void revoke(runner)}
                    >
                      Revoke
                    </button>
                  </div>
                ) : runner.status !== 'active' ? (
                  <span className="agentRunnerRevoked">Revoked</span>
                ) : null}
                {editingScopeRunnerId === runner.agentRunnerId ? (
                  <div className="agentRunnerScopeEditor">
                    <div>
                      <strong>Repositories this machine can check</strong>
                      <p>
                        Only linked repositories selected here can be queued for
                        this runner. Removing access ends queued checks for that
                        repository; a check already running will finish.
                      </p>
                    </div>
                    {repositories.length ? (
                      <div className="agentRunnerRepositoryOptions">
                        {repositories.map((mapping) => (
                          <label key={mapping.projectRepoMappingId}>
                            <input
                              type="checkbox"
                              checked={scopeDraftIds.includes(
                                mapping.projectRepoMappingId
                              )}
                              onChange={(event) =>
                                setScopeDraftIds((current) =>
                                  event.target.checked
                                    ? [...current, mapping.projectRepoMappingId]
                                    : current.filter(
                                        (id) =>
                                          id !== mapping.projectRepoMappingId
                                      )
                                )
                              }
                            />
                            <span>{mapping.externalRepo}</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <p className="agentRunnerEmpty">
                        Link a GitHub repository before granting runner access.
                      </p>
                    )}
                    <div className="agentRunnerActions">
                      <button
                        type="button"
                        className="primaryButton"
                        disabled={busy}
                        onClick={() => void saveRepositoryScope(runner)}
                      >
                        {busy ? 'Saving…' : 'Save access'}
                      </button>
                      <button
                        type="button"
                        className="textButton"
                        disabled={busy}
                        onClick={() => setEditingScopeRunnerId('')}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      )}
      <div className="agentRunnerListHeading">
        <strong>Check a repository</strong>
      </div>
      <form
        className="agentRunnerRegister"
        onSubmit={(event) => void runRepositoryCheck(event)}
      >
        <label>
          <span>Ready runner</span>
          <select
            value={selectedRunnerId}
            onChange={(event) => {
              setSelectedRunnerId(event.target.value);
              setSelectedMappingId('');
            }}
            required
          >
            <option value="">Choose a runner</option>
            {runners
              .filter(
                (runner) =>
                  currentRunnerAvailability(runner) === 'online' &&
                  runner.capabilities.includes('repo.readonly-check') &&
                  (runner.allowedConnectorResourceMappingIds?.length || 0) > 0
              )
              .map((runner) => (
                <option key={runner.agentRunnerId} value={runner.agentRunnerId}>
                  {runner.name}
                </option>
              ))}
          </select>
        </label>
        <label>
          <span>Linked GitHub repository</span>
          <select
            value={selectedMappingId}
            onChange={(event) => setSelectedMappingId(event.target.value)}
            required
          >
            <option value="">Choose a repository</option>
            {repositories
              .filter((mapping) =>
                runners
                  .find((runner) => runner.agentRunnerId === selectedRunnerId)
                  ?.allowedConnectorResourceMappingIds?.includes(
                    mapping.projectRepoMappingId
                  )
              )
              .map((mapping) => (
                <option
                  key={mapping.projectRepoMappingId}
                  value={mapping.projectRepoMappingId}
                >
                  {mapping.externalRepo}
                </option>
              ))}
          </select>
        </label>
        <button
          type="submit"
          className="primaryButton"
          disabled={busy || !selectedRunnerId || !selectedMappingId}
        >
          Check repository
        </button>
      </form>
      <p className="agentRunnerEmpty">
        Only recently checked-in runners with repository access appear here. A
        workspace admin can grant access above.
      </p>
      <div className="agentRunnerListHeading">
        <strong>Recent checks</strong>
        <button
          type="button"
          className="textButton"
          disabled={loading || busy}
          onClick={() => void load()}
        >
          <RefreshCw size={13} />
          Refresh status
        </button>
      </div>
      {jobs.length ? (
        <div className="agentRunnerList">
          {jobs.map((job) => (
            <article className="agentRunnerRow" key={job.agentRunnerJobId}>
              <div className="agentRunnerIdentity">
                <div>
                  <strong>{job.repository}</strong>
                  <span>
                    {runners.find(
                      (runner) => runner.agentRunnerId === job.agentRunnerId
                    )?.name || 'Runner'}{' '}
                    · {jobStatusLabel(job.status)}
                  </span>
                </div>
              </div>
              {job.result ? (
                <div className="agentRunnerAvailability">
                  <span>{job.result.defaultBranch}</span>
                  <small>HEAD {job.result.headCommit.slice(0, 12)}</small>
                </div>
              ) : job.failureCode ? (
                <div className="agentRunnerFailure">
                  <strong>
                    {repositoryCheckFailure(job.failureCode).title}
                  </strong>
                  <span>
                    {repositoryCheckFailure(job.failureCode).nextStep}
                  </span>
                </div>
              ) : (
                <div className="agentRunnerAvailability">
                  <span>
                    {job.status === 'queued'
                      ? 'Waiting for runner'
                      : job.status === 'leased'
                        ? 'Checking repository'
                        : job.status === 'cancel_requested'
                          ? 'Waiting for runner to confirm stop'
                          : 'No result returned'}
                  </span>
                </div>
              )}
              {job.retryOfAgentRunnerJobId ? (
                <small>
                  Retry of check {job.retryOfAgentRunnerJobId.slice(0, 8)}
                </small>
              ) : null}
              {job.status === 'cancel_requested' ? (
                <small>
                  Stop requested
                  {job.cancelRequestedAt
                    ? ` · ${new Date(job.cancelRequestedAt).toLocaleTimeString()}`
                    : ''}
                </small>
              ) : null}
              {job.status === 'cancelled' ? (
                <small>
                  {job.startedAt
                    ? 'Runner confirmed the stop'
                    : 'Cancelled before the runner started'}
                </small>
              ) : null}
              {canManage &&
              (job.status === 'queued' || job.status === 'leased') ? (
                <button
                  type="button"
                  className="textButton"
                  disabled={Boolean(cancellingJobId)}
                  onClick={() => void cancelCheck(job)}
                >
                  {cancellingJobId === job.agentRunnerJobId
                    ? 'Sending request…'
                    : job.status === 'queued'
                      ? 'Cancel check'
                      : 'Request stop'}
                </button>
              ) : null}
              {canManage &&
              (job.status === 'failed' || job.status === 'interrupted') ? (
                <button
                  type="button"
                  className="secondaryButton"
                  disabled={Boolean(retryingJobId)}
                  onClick={() => void retryCheck(job)}
                >
                  {retryingJobId === job.agentRunnerJobId
                    ? 'Queueing…'
                    : 'Retry check'}
                </button>
              ) : null}
              {job.events?.length ? (
                <>
                  <button
                    type="button"
                    className="textButton"
                    aria-expanded={
                      expandedHistoryJobId === job.agentRunnerJobId
                    }
                    onClick={() =>
                      setExpandedHistoryJobId((current) =>
                        current === job.agentRunnerJobId
                          ? ''
                          : job.agentRunnerJobId
                      )
                    }
                  >
                    {expandedHistoryJobId === job.agentRunnerJobId
                      ? 'Hide history'
                      : `History · ${job.events.length}`}
                  </button>
                  {expandedHistoryJobId === job.agentRunnerJobId ? (
                    <ol className="agentRunnerJobHistory">
                      {job.events.map((entry, index) => (
                        <li
                          key={`${entry.eventType}-${entry.created}-${index}`}
                        >
                          <span
                            className={`agentRunnerJobEventDot agentRunnerJobEventDot--${entry.eventType}`}
                          />
                          <span>
                            <strong>{jobEventLabel(entry.eventType)}</strong>
                            <small>
                              {entry.actorUserId
                                ? entry.actorUserId === session?.user?.userId
                                  ? 'You'
                                  : 'Workspace member'
                                : entry.runnerId
                                  ? runners.find(
                                      (runner) =>
                                        runner.agentRunnerId === entry.runnerId
                                    )?.name || 'Runner'
                                  : 'Cindro'}
                            </small>
                          </span>
                          <time dateTime={entry.created}>
                            {entry.created
                              ? new Date(entry.created).toLocaleString()
                              : 'Time unavailable'}
                          </time>
                        </li>
                      ))}
                    </ol>
                  ) : null}
                </>
              ) : null}
            </article>
          ))}
        </div>
      ) : (
        <p className="agentRunnerEmpty">No repository checks yet.</p>
      )}
      <p className="agentRunnerEmpty">
        Waiting checks cancel immediately. For an active check, request a stop;
        the runner confirms it after its current read-only Git lookup. If it
        cannot check back in before its lease expires, the check is marked
        interrupted.
      </p>
      <p className="agentRunnerEmpty">
        Retry uses the runner selected above. Leave it unchanged to use the
        machine from the previous attempt.
      </p>
    </section>
  );
}

function jobEventLabel(
  eventType: NonNullable<AgentRunnerJobRow['events']>[number]['eventType']
) {
  const labels = {
    queued: 'Check queued',
    retry_queued: 'Retry queued',
    claimed: 'Runner started the check',
    succeeded: 'Check completed',
    failed: 'Check failed',
    interrupted: 'Runner stopped responding',
    cancel_requested: 'Stop requested',
    cancelled: 'Check cancelled',
    repository_access_removed: 'Repository access removed'
  } as const;
  return labels[eventType];
}

function jobStatusLabel(status: AgentRunnerJobRow['status']) {
  const labels: Record<AgentRunnerJobRow['status'], string> = {
    queued: 'Waiting for runner',
    leased: 'Checking repository',
    cancel_requested: 'Stop requested',
    succeeded: 'Check complete',
    failed: 'Check failed',
    interrupted: 'Runner stopped responding',
    cancelled: 'Cancelled'
  };
  return labels[status];
}
